import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BdResource,
  BdResourceService,
} from '@bindoc-projects/resource-domain-lib';
import { BdLoaderState } from '@bindoc-projects/platform-lib/data';
import { BdMessageEmptyComponent } from '@bindoc-projects/ui-lib/feedback/ui';
import { ButtonDirective } from 'primeng/button';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { switchMap, take } from 'rxjs';
import { FeatureResourceRequest } from '../core/abstract-feature-resource.service';

export interface BdFeatureResourceSnippetSaveDialogData<
  TContent,
  TResource extends BdResource,
> {
  service: BdResourceService<TResource, FeatureResourceRequest<TContent>>;
  /**
   * Currently loaded resource — when set, the dialog offers update + "save as
   * new"; when undefined, only create-as-new is available.
   */
  resource?: TResource;
  /**
   * Snapshot of the live feature content to persist. The dialog never reads
   * `resource.content` for saving because the loaded resource may be stale
   * relative to the current page state.
   */
  content: TContent;
}

@Component({
  selector: 'bd-feature-resource-snippet-save-dialog',
  template: `
    <div class="flex flex-col gap-4">
      @if (state() === BdLoaderState.ERROR) {
        <bd-message-empty
          icon="pi pi-times-circle"
          message="Der Vorgang wurde abgebrochen"
        ></bd-message-empty>
      } @else {
        <div class="flex flex-col gap-2">
          <label
            class="text-sm font-medium"
            for="feature-resource-snippet-save-dialog-name"
          >
            Name
          </label>
          <input
            id="feature-resource-snippet-save-dialog-name"
            pInputText
            data-test="feature-resource-snippet-save-dialog-name-input"
            placeholder="Name der Analyse"
            [formControl]="nameCtrl"
          />
        </div>
      }

      <div class="flex flex-row gap-2 justify-end">
        <button
          pButton
          text
          severity="secondary"
          (click)="ref.close(null)"
          [disabled]="isBusy()"
        >
          Abbrechen
        </button>
        @if (data.resource) {
          <button
            pButton
            severity="secondary"
            data-test="feature-resource-snippet-save-dialog-save-as-new-button"
            [disabled]="!nameCtrl.valid || isBusy()"
            (click)="saveAsNew()"
          >
            Als neue Analyse speichern
          </button>
          <button
            pButton
            severity="primary"
            bdDialogPrimaryAction
            data-test="feature-resource-snippet-save-dialog-update-button"
            [disabled]="!nameCtrl.valid || isBusy()"
            (click)="update()"
          >
            Speichern
          </button>
        } @else {
          <button
            pButton
            severity="primary"
            bdDialogPrimaryAction
            data-test="feature-resource-snippet-save-dialog-save-button"
            [disabled]="!nameCtrl.valid || isBusy()"
            (click)="create()"
          >
            Speichern
          </button>
        }
      </div>
    </div>
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    BdMessageEmptyComponent,
    ButtonDirective,
    InputTextModule,
    ReactiveFormsModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BdFeatureResourceSnippetSaveDialogComponent<
  TContent,
  TResource extends BdResource,
> implements OnInit {
  protected readonly BdLoaderState = BdLoaderState;

  readonly nameCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  readonly state = signal<BdLoaderState>(BdLoaderState.INIT);

  private readonly _config: DynamicDialogConfig<
    BdFeatureResourceSnippetSaveDialogData<TContent, TResource>
  > = inject(DynamicDialogConfig);
  private readonly _destroyRef = inject(DestroyRef);
  readonly ref: DynamicDialogRef = inject(DynamicDialogRef);

  protected readonly data = this._config.data;

  ngOnInit(): void {
    if (this.data.resource?.name) {
      this.nameCtrl.setValue(this.data.resource.name);
    }
  }

  isBusy(): boolean {
    return this.state() === BdLoaderState.LOADING;
  }

  create(): void {
    const name = this.nameCtrl.value.trim();
    if (!name) {
      return;
    }
    this.state.set(BdLoaderState.LOADING);
    this.data.service
      .create({ name, content: this.data.content })
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (saved) => {
          this.state.set(BdLoaderState.SUCCESS);
          this.ref.close(saved);
        },
        error: () => this.state.set(BdLoaderState.ERROR),
      });
  }

  saveAsNew(): void {
    this.create();
  }

  update(): void {
    const existing = this.data.resource;
    if (!existing) {
      this.create();
      return;
    }
    const name = this.nameCtrl.value.trim();
    if (!name) {
      return;
    }
    const updated = existing.clone() as TResource;
    updated.name = name;
    // The resource's `content` field carries the to-be-persisted payload for
    // the four BDA-5126 services because their `_toContent(resource)`
    // simply reads `resource.content`.
    (updated as TResource & { content?: TContent }).content = this.data.content;

    this.state.set(BdLoaderState.LOADING);
    this.data.service
      .update(updated)
      .pipe(
        switchMap(() => this.data.service.findById(updated.id)),
        take(1),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe({
        next: (refreshed) => {
          this.state.set(BdLoaderState.SUCCESS);
          this.ref.close(refreshed as TResource);
        },
        error: () => this.state.set(BdLoaderState.ERROR),
      });
  }
}
